import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontalIcon } from 'lucide-react';
import type { SortKey, VehicleFilters } from '../types/vehicle';
import { emptyFilters } from '../types/vehicle';
import { useVehicles } from '../contexts/VehiclesContext';
import { filterVehicles } from '../utils/vehicles';
import { useSeo } from '../utils/seo';
import { CatalogFilters } from '../components/CatalogFilters';
import { VehicleCard } from '../components/VehicleCard';
import { CatalogSourceNotice } from '../components/CatalogSourceNotice';
import { ConsultaForm } from '../components/forms/ConsultaForm';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/SearchBar';
const sortOptions: {
  value: SortKey;
  label: string;
}[] = [{
  value: 'destacados',
  label: 'Destacados primero'
}, {
  value: 'recientes',
  label: 'Más recientes'
}, {
  value: 'precio-asc',
  label: 'Menor precio'
}, {
  value: 'precio-desc',
  label: 'Mayor precio'
}];
function filtersFromParams(params: URLSearchParams, base: VehicleFilters): VehicleFilters {
  const text = (key: string, current: string) => params.get(key) ?? current;
  return {
    ...base,
    q: params.get('q') ?? '',
    marca: text('marca', base.marca),
    modelo: text('modelo', base.modelo),
    estado: text('estado', base.estado),
    tipo: text('tipo', base.tipo),
    combustible: text('combustible', base.combustible),
    transmision: text('transmision', base.transmision),
    anioDesde: text('anioDesde', base.anioDesde),
    anioHasta: text('anioHasta', base.anioHasta),
    precioMin: text('precioMin', base.precioMin),
    precioMax: text('precioMax', base.precioMax),
    kmMax: text('kmMax', base.kmMax),
    financiacion: params.get('financiacion') === '1' || base.financiacion,
    permuta: params.get('permuta') === '1' || base.permuta,
    oportunidad: params.get('oportunidad') === '1' || base.oportunidad
  };
}
export function Catalogo() {
  const {
    vehicles,
    loading
  } = useVehicles();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<VehicleFilters>(() => filtersFromParams(params, emptyFilters));
  const [showFilters, setShowFilters] = useState(false);
  useSeo('Catálogo VCARS | Usados seleccionados y 0KM', 'Buscá entre usados seleccionados y 0KM: filtrá por marca, modelo, año, precio, kilometraje, financiación y permuta.');

  // Keep the hero search in sync with the catalogue when the URL changes.
  useEffect(() => {
    setFilters(prev => filtersFromParams(params, prev));
  }, [params]);
  const results = useMemo(() => filterVehicles(vehicles, filters), [vehicles, filters]);
  const patch = (p: Partial<VehicleFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...p
    }));
    // The URL only seeds the filters: once the user edits a field, drop its param
    // so the incoming value is not re-applied on the next sync.
    const next = new URLSearchParams(params);
    let changed = false;
    Object.keys(p).forEach(key => {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    });
    if ('q' in p) {
      if (p.q) next.set('q', String(p.q));
      changed = true;
    }
    if (changed) setParams(next, {
      replace: true
    });
  };
  const reset = () => {
    setFilters({
      ...emptyFilters,
      sort: filters.sort
    });
    setParams(new URLSearchParams(), {
      replace: true
    });
  };
  return <main className="bg-ink" data-id="element-641" data-mp="2cg5z1h4au624">
      <div className="border-b border-line bg-surface" data-id="element-642" data-mp="2h98w3z8abxn2">
        <div className="mx-auto w-full max-w-7xl px-5 py-12 md:px-8 md:py-16" data-id="element-643" data-mp="2hascdptxy8ou">
          <h1 className="text-4xl font-black tracking-tight text-white md:text-[52px]" data-id="element-644" data-mp="23ivndunkkewo">
            Catálogo VCARS
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-mist md:text-lg" data-id="element-645" data-mp="27gjlt7oh0ptb">
            Usados seleccionados y 0KM revisados unidad por unidad. Filtrá por marca,
            modelo, presupuesto o condiciones de operación.
          </p>
          <div className="mt-8 max-w-3xl" data-id="element-646" data-mp="30ukmmvktrvhv">
            <SearchBar size="md" showSuggestions={false} data-id="element-647" data-mp="30xqak7hpwrc0" />
          </div>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-7xl gap-8 px-5 py-10 md:px-8 lg:grid-cols-[300px_1fr]" data-id="element-648" data-mp="2ww5351nyd597">
        <div data-id="element-649" data-mp="2s0z89dckog8z">
          <Button variant="outline" size="md" className="w-full lg:hidden" onClick={() => setShowFilters(v => !v)} aria-expanded={showFilters} data-id="element-650" data-mp="2n48x94fmkauf">
            <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-651" data-mp="2d5jj9mqunajg" />
            {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>
          <div className={`mt-4 lg:mt-0 lg:sticky lg:top-24 ${showFilters ? 'block' : 'hidden lg:block'}`} data-id="element-652" data-mp="296kb7t9tmxns">
            <CatalogFilters filters={filters} onChange={patch} onReset={reset} data-id="element-653" data-mp="7bs06qw5qr2g" />
          </div>
        </div>

        <div data-id="element-654" data-mp="2ejgglvd2ez3">
          <CatalogSourceNotice data-id="element-655" data-mp="2err0740h874" />
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4" data-id="element-656" data-mp="9xspgdzygbnm">
            <p className="text-sm text-mist" data-id="element-657" data-mp="9volxx5yxenv">
              {loading ? 'Actualizando catálogo…' : <>
                  <span className="font-bold text-white" data-id="element-658" data-mp="jonaxg5dn780">{results.length}</span>{' '}
                  {results.length === 1 ? 'vehículo disponible' : 'vehículos disponibles'}
                </>}
            </p>
            <div className="flex items-center gap-2" data-id="element-659" data-mp="3s2m7aiw9gum1">
              <label htmlFor="sort" className="text-[12px] font-semibold uppercase tracking-wide text-mist" data-id="element-660" data-mp="3s3e39utrsucu">
                Ordenar por
              </label>
              <select id="sort" value={filters.sort} onChange={e => patch({
              sort: e.target.value as SortKey
            })} className="h-10 rounded-xl border border-line bg-surface2/70 px-3 text-sm text-white focus:border-neon" data-id="element-661" data-mp="4wboyjldur7k">
                {sortOptions.map(o => <option key={o.value} value={o.value} data-id="element-662" data-mp="38fny9ya8fbdr">
                    {o.label}
                  </option>)}
              </select>
            </div>
          </div>

          {loading ? <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-663" data-mp="3hgvv0bx3o45q">
              {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="h-[420px] animate-pulse rounded-2xl border border-line bg-surface2/40" data-id="element-664" data-mp="2tqrr03g022e7" />)}
            </div> : results.length > 0 ? <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-665" data-mp="33hw38veaj95c">
              {results.map(v => <VehicleCard key={v.id} vehicle={v} data-id="element-666" data-mp="2azqrneayw4u4" />)}
            </div> : <div className="space-y-6" data-id="element-667" data-mp="260xexo3i8hv0">
              <div className="rounded-2xl border border-line bg-surface2/40 p-8 text-center" data-id="element-668" data-mp="25zw5fo2503fe">
                <p className="text-lg font-semibold text-white" data-id="element-669" data-mp="lhtmonvkvq80">
                  No encontramos vehículos con esa búsqueda. Dejanos tu consulta y te
                  ayudamos a encontrarlo.
                </p>
                <Button variant="outline" size="md" className="mt-5" onClick={reset} data-id="element-670" data-mp="cji1x2he7mnh">
                  Limpiar filtros
                </Button>
              </div>
              <ConsultaForm title="Contanos qué vehículo buscás" lead="Recibimos unidades nuevas todas las semanas. Te avisamos cuando aparezca lo que necesitás." motivoInicial="Quiero comprar un auto" vehiculoInicial={filters.q} data-id="element-671" data-mp="156ci3n76fzv5" />
            </div>}
        </div>
      </div>
    </main>;
}